import React from 'react';

import { ActivityMetrics } from 'components/SaloonLobby/types';

import styles from './ActivityStrip.module.scss';

export type ActivityStripStatus = 'loading' | 'fresh' | 'stale' | 'unavailable';

interface ActivityStripProps {
  // Absent while status is 'loading'/'unavailable' (no successful CDN read
  // yet). Present (and rendered) for 'fresh' AND 'stale' — a stale reading
  // still shows the last good numbers, just flagged, rather than
  // disappearing.
  metrics?: ActivityMetrics;
  status: ActivityStripStatus;
  // Only rendered (as a keyboard-accessible Retry button) while status is
  // 'unavailable' — see that branch's own comment. Optional so a caller with
  // nothing to retry (Storybook, most existing tests) can omit it entirely.
  onRetry?: () => void;
  // A class for the strip's root, whichever state it renders, so the lobby can
  // place it (filter bar, collapsing row) with a class the lobby owns instead
  // of matching this module's generated ident (review, 2026-09-16).
  className?: string;
  // EN-10885: the ops.lobby-cash-in-play flag, read by the lobby so this atom
  // stays hook-free. Absent is off: the In Play stat is hidden.
  cashInPlayEnabled?: boolean;
}

const formatMoney = (value: number): string =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Singular label when the count is exactly 1 (e.g. "1 Table", "1 Player").
const pluralize = (count: number, singular: string): string =>
  count === 1 ? singular : `${singular}s`;

// EN-10885: Ryan wants cash in play withheld while the lobby is quiet. Kept
// separate from the Activity Strip gate: tables and players remain useful
// below the floor, while this stat has its own flag so product can remove it
// again without a release.
export const CASH_IN_PLAY_THRESHOLD = 200_000;

export const shouldShowCashInPlay = (inPlay: number, enabled: boolean): boolean =>
  enabled && inPlay > CASH_IN_PLAY_THRESHOLD;

const ActivityStrip: React.FC<ActivityStripProps> = ({
  metrics,
  status,
  onRetry,
  className,
  cashInPlayEnabled = false
}) => {
  const rootClass = className ? `${styles.strip} ${className}` : styles.strip;
  // Distinct from 'loading': the feed has been polling without a single
  // successful read for long enough that "still loading" would be
  // misleading — see SaloonLobbyContainer's own isActivityUnavailable
  // comment for the threshold. Plain wording, no error code/jargon.
  //
  // A Retry button IS shown here (2026-09-15 review), despite the background
  // poll already recovering on its own the moment a real reading lands: a
  // player has no way to tell whether that recovery is imminent or the feed
  // has gone quiet for good, and the ux-accessibility standard requires a
  // data-fetching error state to expose a player-initiated recovery action,
  // not just a passive one. Clicking it dispatches requestActivityStripRetry
  // (via onActivityRetry, wired through SaloonLobbyContainer), which restarts
  // useActivityStripFeed's poll immediately rather than waiting out whatever
  // backoff it's currently on — see that hook's retrySignal parameter.
  // `<button>`, not a link: there is nowhere to navigate to, only an action
  // to perform, and `<button>` is keyboard-activatable (Enter/Space) and
  // focusable by default with no extra ARIA needed.
  if (status === 'unavailable') {
    return (
      <div className={rootClass} data-status='unavailable' role='status' aria-live='polite'>
        <span className={styles.loadingLabel}>Table activity unavailable right now</span>
        {onRetry && (
          <button type='button' className={styles.retryButton} onClick={onRetry}>
            Retry
          </button>
        )}
      </div>
    );
  }

  // Guard clause, not just a status check: a 'fresh'/'stale' status with no
  // metrics would otherwise crash on metrics.tablesActive below. Shouldn't
  // happen given how SaloonLobbyContainer derives status, but this component
  // has no other way to defend its own render.
  if (status === 'loading' || !metrics) {
    return (
      <div className={rootClass} data-status='loading' role='status' aria-live='polite'>
        <span className={styles.loadingLabel}>Loading table activity…</span>
      </div>
    );
  }

  const isStale = status === 'stale';
  const showCashInPlay = shouldShowCashInPlay(metrics.inPlay, cashInPlayEnabled);

  return (
    <div className={rootClass} data-status={status}>
      {isStale && (
        // A SIBLING assertive alert, not nested inside the polite status
        // region below: some assistive technology coalesces a nested live
        // region into its ancestor's own politeness, which would delay or
        // suppress this announcement entirely — a connection-loss signal a
        // screen-reader user could otherwise miss, or hear long after it
        // happened, riding the enclosing polite region. Kept as a visual
        // sibling (not hoisted above `.strip`) so it stays in the same flex
        // row as the metrics — see `.metrics`' own comment in the stylesheet
        // for how the layout is preserved despite the two roles no longer
        // sharing a DOM ancestor/descendant relationship.
        <span
          className={styles.staleBadge}
          role='alert'
          aria-live='assertive'
          title='The live feed is unreachable — showing the last known numbers.'>
          Reconnecting
        </span>
      )}
      <div className={styles.metrics} role='status' aria-live='polite'>
        <div className={styles.stat}>
          <span
            className={isStale ? `${styles.pulse} ${styles.pulseStale}` : styles.pulse}
            aria-hidden
          />
          <span className={styles.num}>{metrics.tablesActive}</span>
          <span className={styles.label}>{pluralize(metrics.tablesActive, 'Table')}</span>
        </div>
        <span className={styles.sep} aria-hidden>
          ✦
        </span>
        <div className={styles.stat}>
          <span className={styles.num}>{metrics.playersSeated}</span>
          <span className={styles.label}>{pluralize(metrics.playersSeated, 'Player')}</span>
        </div>
        {showCashInPlay && (
          <>
            <span className={styles.sep} aria-hidden>
              ✦
            </span>
            <div className={styles.stat}>
              <span className={styles.num}>{formatMoney(metrics.inPlay)}</span>
              <span className={styles.label}>In Play</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ActivityStrip;
